import { useState } from 'react';
import type { TimelineViewData } from '@shared/types/views';
import { ExpandableText } from '@/components/ui/ExpandableText';
import { ToneDot } from '@/components/views/atoms/ToneDot';
import { cn } from '@/lib/utils';
import { groupByMonth, timelineDate } from './timeline-format';

/** Events shown before « Voir les N autres ». */
const TIMELINE_VISIBLE = 12;
/** Events from which the timeline is cut by month. */
const MONTH_HEADERS_FROM = 8;

interface TimelineViewProps {
  hideTitle?: boolean;
  data: TimelineViewData;
}

/**
 * Timeline (layout lab, lot 3b): dated events in the order given, on one rail;
 * a long timeline is cut by month. Planned events are drawn hollow and say
 * « À venir ». Date and kind sit above the event so it reads in any width.
 */
export const TimelineView = ({ data, hideTitle }: TimelineViewProps) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? data.events : data.events.slice(0, TIMELINE_VISIBLE);
  // Month headers help a long timeline only; a short one reads its dates.
  const showMonths = data.events.length > MONTH_HEADERS_FROM && groupByMonth(data.events).length > 1;
  const groups = showMonths ? groupByMonth(shown) : [{ month: '', events: shown }];
  return (
    <div className="min-w-0">
      {!hideTitle && data.title && <p className="text-sm font-semibold text-domino-ink">{data.title}</p>}
      {data.insight && <p className="mt-1 text-sm leading-6 text-domino-ink">{data.insight}</p>}
      {data.source_note && <p className="mt-1 text-xs leading-5 text-domino-inkSoft">{data.source_note}</p>}
      {data.events.length === 0 ? (
        <p className="mt-3 text-sm text-domino-inkSoft">
          {data.empty_message ?? 'Aucun événement daté.'}
        </p>
      ) : (
        <div className="mt-3 space-y-4">
          {groups.map((group, groupIndex) => (
            <section key={`${group.month}-${groupIndex}`} aria-label={group.month || undefined}>
              {group.month && (
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-domino-inkSoft first-letter:uppercase">{group.month}</h4>
              )}
              <ol className="relative">
                {group.events.map((event, index) => {
                  const date = timelineDate(event.date);
                  const last = index === group.events.length - 1;
                  return (
                    <li key={`${event.date}-${index}`} className="relative grid grid-cols-[0.75rem_1fr] gap-x-3 pb-4 last:pb-0">
                      <div className="relative flex justify-center">
                        {!last && <span aria-hidden="true" className="absolute bottom-[-1rem] top-4 w-px bg-domino-cardBorder" />}
                        <ToneDot tone={event.tone ?? 'neutral'} hollow={date.upcoming}
                          className={cn('relative mt-1.5', date.upcoming && 'text-domino-aubergine', !event.tone && !date.upcoming && 'bg-domino-aubergine')} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs leading-5 text-domino-inkSoft">
                          <span className="tabular-nums">{date.label}{date.time ? ` · ${date.time}` : ''}</span>
                          {event.kind && ` · ${event.kind}`}
                          {date.upcoming && <span className="font-medium text-domino-aubergine"> · À venir</span>}
                        </p>
                        <p className="text-sm font-medium leading-6 text-domino-ink">{event.label}</p>
                        {event.detail && (
                          <ExpandableText text={event.detail} collapsedLines={3} minCharactersToToggle={220} className="text-sm leading-6 text-domino-inkSoft" />
                        )}
                        {event.url && (
                          <a href={event.url} target="_blank" rel="noopener noreferrer" className="text-xs text-domino-prune underline hover:text-domino-aubergine">
                            Voir la source
                          </a>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          ))}
          {data.events.length > TIMELINE_VISIBLE && (
            <button type="button" onClick={() => setExpanded((current) => !current)} className="text-sm font-medium text-domino-aubergine hover:text-domino-prune">
              {expanded ? 'Voir moins' : `Voir les ${data.events.length - TIMELINE_VISIBLE} autres`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
